import type { NutrientsPer100g } from './data/fsanz-types'
export type { NutrientsPer100g }

/** Child profile (Screen 2) */
export interface ChildProfile {
  /** Stable UUID — assigned on creation, never changes. */
  id: string
  firstName: string
  age: string
  sex: '' | 'male' | 'female'
  weightKg: string
  dietaryRequirements: string[]
  dietaryOther: string
}

/** Single training session (Screen 3) */
export interface TrainingSession {
  id: string
  isRestDay: boolean
  /** Whether this is a training session or a competition/event. Defaults to 'training'. */
  sessionType: 'training' | 'competition'
  time: string
  intensity: 'light' | 'moderate' | 'hard'
  /** Duration in minutes (exact value, e.g. 45, 90). */
  durationMinutes: number
}

export const DIETARY_OPTIONS = [
  'Vegetarian',
  'Vegan',
  'Gluten intolerant',
  'Dairy intolerant',
  'Peanut allergy',
  'Nut allergy',
  'Egg allergy',
  'Fish allergy',
  'Shellfish allergy',
  'Sesame allergy',
  'Soy allergy',
  'Halal',
  'Kosher',
] as const

export function createEmptySession(id: string): TrainingSession {
  return {
    id,
    isRestDay: false,
    sessionType: 'training',
    time: '',
    intensity: 'moderate',
    durationMinutes: 60,
  }
}

/** Meal slot ids used for logging (must match fuel plan slot ids). */
export type MealSlotId = 'breakfast' | 'lunch' | 'dinner' | 'pre' | 'during' | 'recovery' | `pre-${number}` | `during-${number}`

/** Source of the logged meal: suggested example, past history, or free text. */
export type MealLogSource = 'example' | 'history' | 'other'

/** One food from the database (id + amount in grams) for nutrient lookup. */
export interface FoodLogEntry {
  foodId: string
  amountGrams: number
  /** Inline per-100g nutrients for barcode-scanned products not in the FSANZ database.
   *  When present, nutrient lookup uses this directly instead of getFoodById(). */
  per100g?: NutrientsPer100g
  /** Display name for barcode-scanned products (getFoodById will return undefined for them). */
  productName?: string
  /** True when the product is a liquid and should display mL instead of g. */
  isLiquid?: boolean
}

/** One logged meal for a slot (what they ate, when, approximate amount). */
export interface MealLogEntry {
  /** Time eaten (HH:mm). */
  timeEaten: string
  source: MealLogSource
  /** Display name of the meal (e.g. example name or custom text). */
  mealName: string
  /** Optional approximate amount (e.g. "1 bowl", "small portion"). */
  amount?: string
  /** When set, this entry is linked to FSANZ/AUSNUT: use for nutrient lookup. */
  foodId?: string
  /** Amount in grams for FSANZ nutrient calculation (required when foodId is set). */
  amountGrams?: number
  /** Multiple foods from the database (nutrients summed). Use this when logging composite meals. */
  foodEntries?: FoodLogEntry[]
  /** True when this entry was logged from a saved recipe (show recipe name instead of ingredient list). */
  fromRecipe?: boolean
}

/** Logs for one day keyed by slot id. */
export type DailyMealLog = Partial<Record<MealSlotId, MealLogEntry>>

/** One additional (non-slot) snack log. */
export interface ExtraSnackLog {
  id: string
  entry: MealLogEntry
}

/** One ingredient extracted by AI from a recipe photo. */
export interface ExtractedIngredient {
  /** Raw text from the recipe as Claude saw it, e.g. "2 cups plain flour" */
  originalText: string
  /** Normalised ingredient name, e.g. "plain flour" */
  name: string
  /** AI's gram estimate for the recipe quantity */
  estimatedGrams: number
}

export type MealTag = 'breakfast' | 'lunch' | 'dinner' | 'snack'

/** A user-saved recipe (named set of food entries for quick re-logging). */
export interface SavedRecipe {
  id: string
  name: string
  foodEntries: FoodLogEntry[]
  createdAt: string
  /** Number of serves this recipe makes. */
  servings?: number
  /** Compressed thumbnail dataUrl or Supabase signed URL. */
  photoThumbnailUrl?: string
  /** Original ingredients extracted from a recipe photo by AI. */
  extractedIngredients?: ExtractedIngredient[]
  /** Meal types this recipe is associated with. Untagged recipes appear in all slots. */
  mealTags?: MealTag[]
}

/** One water/drink log entry (amount in mL). */
export interface HydrationLogEntry {
  id: string
  amountMl: number
  /** e.g. "water", "milk", "sports drink" */
  description: string
}

/** How the child felt during/after a training session. */
export type TrainingFeedbackEnergy = 'low' | 'good' | 'great'

export const FEEDBACK_ISSUES = [
  'Hungry during session',
  'Stomach discomfort',
  'Felt full/heavy',
] as const

export type FeedbackIssue = (typeof FEEDBACK_ISSUES)[number]

export interface TrainingFeedbackEntry {
  energyLevel: TrainingFeedbackEnergy
  issues: FeedbackIssue[]
  notes: string
}

/** Training feedback per session, keyed by session id. */
export type DailyTrainingFeedback = Record<string, TrainingFeedbackEntry>
